import { apiRequest } from '@/lib/api/client'
import {
  normalizeCampaignQuestion,
  normalizeCampaignQuestions,
} from '@/lib/campaign-question-stats'
import type {
  CampaignQuestion,
  CampaignQuestionAnswersResponse,
  CampaignQuestionInput,
  CampaignQuestionListResponse,
  CampaignQuestionResponse,
} from '@/types/campaign-question'

export async function fetchCampaignQuestionAnswers(
  campaignId: number,
  questionId: number,
  token: string,
  filters: { page: number; optionId: string; search: string },
  signal?: AbortSignal
): Promise<CampaignQuestionAnswersResponse> {
  const params = new URLSearchParams({ page: String(filters.page), per_page: '25' })
  if (filters.optionId) params.set('option_id', filters.optionId)
  if (filters.search) params.set('search', filters.search)
  return apiRequest<CampaignQuestionAnswersResponse>(
    `/api/campaigns/${campaignId}/questions/${questionId}/responses?${params}`,
    { method: 'GET', token, signal }
  )
}

export async function fetchCampaignQuestions(
  campaignId: number,
  token: string
): Promise<CampaignQuestion[]> {
  const response = await apiRequest<CampaignQuestionListResponse>(
    `/api/campaigns/${campaignId}/questions`,
    {
      method: 'GET',
      token,
    }
  )
  return normalizeCampaignQuestions(response.questions ?? [])
}

export async function createCampaignQuestion(
  campaignId: number,
  data: CampaignQuestionInput,
  token: string
): Promise<CampaignQuestion | null> {
  const response = await apiRequest<CampaignQuestionResponse>(
    `/api/campaigns/${campaignId}/questions`,
    {
      method: 'POST',
      body: JSON.stringify(data),
      token,
    }
  )
  const question = response.question
  return question ? normalizeCampaignQuestion(question) : null
}

export async function updateCampaignQuestion(
  questionId: number,
  data: Partial<CampaignQuestionInput>,
  token: string
): Promise<CampaignQuestion | null> {
  const response = await apiRequest<CampaignQuestionResponse>(
    `/api/questions/${questionId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
      token,
    }
  )
  const question = response.question
  return question ? normalizeCampaignQuestion(question) : null
}

export async function deleteCampaignQuestion(
  questionId: number,
  token: string
): Promise<void> {
  await apiRequest<{ message?: string }>(`/api/questions/${questionId}`, {
    method: 'DELETE',
    token,
  })
}
